import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AiGrant,
  AiGrantAccess,
  AiGrantListItem,
  DrawingAccessRole,
  DrawingShareCreateResponse,
  DrawingShareReadResponse,
} from "@excalidraw-ms/shared";
import * as backendClient from "../api/backendClient";
import type { DrawingItem } from "../hooks/useGalleryData";
import CollabShareDialog from "./CollabShareDialog";
import DestructiveConfirmationDialog from "./DestructiveConfirmationDialog";
import { PeopleLinkPreparationError, readPeopleSharing } from "../utils/readPeopleSharing";
import { copyLink } from "../utils/copyLink";
import { useDrawingReference } from "../hooks/useDrawingReference";

interface GalleryShareDialogProps {
  drawing: DrawingItem;
  accountId?: string;
  getToken: () => Promise<string>;
  onPeopleAccessResolved: (drawingId: string, isShared: boolean) => void;
  onAiAccessResolved: (
    drawing: DrawingItem,
    grant: AiGrantListItem | null,
    access: AiGrantAccess | null,
  ) => void;
  onAiShared: (drawing: DrawingItem, grant: AiGrant, access: AiGrantAccess) => void;
  onAiRevoked: (code: string) => void;
  onConnect: () => void;
  onClose: () => void;
}

export default function GalleryShareDialog({
  drawing,
  accountId,
  getToken,
  onPeopleAccessResolved,
  onAiAccessResolved,
  onAiShared,
  onAiRevoked,
  onConnect,
  onClose,
}: GalleryShareDialogProps) {
  const isRecipient = drawing.role !== "owner";
  const reference = useDrawingReference({
    drawingId: drawing.drawingId,
    accountId,
    enabled: isRecipient,
    getToken,
  });
  const [peopleLoading, setPeopleLoading] = useState(drawing.role === "owner");
  const [peopleLoadError, setPeopleLoadError] = useState<string | null>(null);
  const [peopleRetryLabel, setPeopleRetryLabel] = useState("Retry people access");
  const peoplePreparationRetry = useRef<(() => Promise<DrawingShareReadResponse>) | null>(null);
  const [peopleRevision, setPeopleRevision] = useState(0);
  const [aiLoading, setAiLoading] = useState(true);
  const [aiLoadError, setAiLoadError] = useState<string | null>(null);
  const [aiRevision, setAiRevision] = useState(0);
  const [links, setLinks] = useState<Partial<Record<DrawingAccessRole, string>>>({});
  const [role, setRole] = useState<DrawingAccessRole>("editor");
  const [sharedWithPeople, setSharedWithPeople] = useState<boolean | undefined>(
    drawing.role === "owner" ? undefined : true,
  );
  const [peopleSharingKind, setPeopleSharingKind] = useState<
    DrawingShareReadResponse["peopleSharingKind"] | undefined
  >(drawing.role === "owner" ? undefined : "recipient");
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(drawing.aiGrantCode ?? null);
  const [access, setAccess] = useState<AiGrantAccess | undefined>(drawing.aiGrantAccess);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const linkPending = useRef(false);
  const revokePending = useRef(false);
  const opener = useRef(document.activeElement);
  const onPeopleAccessResolvedRef = useRef(onPeopleAccessResolved);
  const onAiAccessResolvedRef = useRef(onAiAccessResolved);
  useEffect(() => {
    onPeopleAccessResolvedRef.current = onPeopleAccessResolved;
  }, [onPeopleAccessResolved]);

  useEffect(() => {
    onAiAccessResolvedRef.current = onAiAccessResolved;
  }, [onAiAccessResolved]);

  useEffect(() => {
    const element = opener.current;
    return () => {
      if (element instanceof HTMLElement && element.isConnected) element.focus();
    };
  }, []);

  useEffect(() => {
    if (drawing.role !== "owner") {
      setPeopleLoading(false);
      setPeopleLoadError(null);
      setSharedWithPeople(true);
      setPeopleSharingKind("recipient");
      return;
    }
    let cancelled = false;
    setPeopleLoading(true);
    setPeopleLoadError(null);
    setPeopleRetryLabel("Retry people access");
    peoplePreparationRetry.current = null;
    const applySharing = (sharing: DrawingShareReadResponse) => {
      const currentLinks: Partial<Record<DrawingAccessRole, string>> = {};
      sharing.links.forEach((link) => {
        currentLinks[link.role] = link.shareUrl;
      });
      setLinks(currentLinks);
      setRole(currentLinks.editor ? "editor" : currentLinks.viewer ? "viewer" : "editor");
    };
    void (async () => {
      try {
        const token = await getToken();
        const sharing = await readPeopleSharing(token, drawing.drawingId, (isShared, kind) => {
          if (cancelled) return false;
          setSharedWithPeople(isShared);
          setPeopleSharingKind(kind);
          onPeopleAccessResolvedRef.current(drawing.drawingId, isShared);
          return true;
        });
        if (cancelled) return;
        applySharing(sharing);
      } catch (error) {
        console.error("[GalleryShareDialog] People access refresh failed:", error);
        if (!cancelled) {
          if (error instanceof PeopleLinkPreparationError) {
            applySharing(error.sharing);
            peoplePreparationRetry.current = error.retry;
            setPeopleRetryLabel("Retry link preparation");
            setPeopleLoadError("A viewer invitation link could not be prepared. Please try again.");
          } else {
            setPeopleLoadError("People sharing details could not be loaded. Please try again.");
          }
        }
      } finally {
        if (!cancelled) setPeopleLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [drawing.drawingId, drawing.role, getToken, peopleRevision]);

  const retryPeopleAccess = () => {
    const retryPreparation = peoplePreparationRetry.current;
    if (!retryPreparation) {
      setPeopleRevision((current) => current + 1);
      return;
    }
    setPeopleLoading(true);
    setPeopleLoadError(null);
    void retryPreparation()
      .then((sharing) => {
        const currentLinks: Partial<Record<DrawingAccessRole, string>> = {};
        sharing.links.forEach((link) => {
          currentLinks[link.role] = link.shareUrl;
        });
        setLinks(currentLinks);
        setRole(currentLinks.editor ? "editor" : currentLinks.viewer ? "viewer" : "editor");
        peoplePreparationRetry.current = null;
        setPeopleRetryLabel("Retry people access");
      })
      .catch((error: unknown) => {
        console.error("[GalleryShareDialog] People invitation preparation failed:", error);
        setPeopleLoadError("A viewer invitation link could not be prepared. Please try again.");
      })
      .finally(() => setPeopleLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setAiLoading(true);
    setAiLoadError(null);
    void (async () => {
      try {
        const token = await getToken();
        const grants = await backendClient.lookupAiGrants(token, [drawing.drawingId]);
        if (!cancelled) {
          const resolved =
            grants.grants.find((candidate) => candidate.drawingId === drawing.drawingId) ?? null;
          setCode(resolved?.code ?? null);
          setAccess(resolved?.access ?? undefined);
          onAiAccessResolvedRef.current(drawing, resolved, resolved?.access ?? null);
        }
      } catch (error) {
        console.error("[GalleryShareDialog] AI access lookup failed:", error);
        if (!cancelled) setAiLoadError("AI access could not be checked. Please try again.");
      } finally {
        if (!cancelled) setAiLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [aiRevision, drawing, getToken]);

  const resolveLink = async (nextRole: DrawingAccessRole) => {
    if (isRecipient) throw new Error("Only the owner can create invitations");
    if (linkPending.current) return;
    setLinkError(null);
    if (links[nextRole]) {
      setRole(nextRole);
      return;
    }
    linkPending.current = true;
    setLinkBusy(true);
    try {
      const token = await getToken();
      const result: DrawingShareCreateResponse = await backendClient.createDrawingShare(
        token,
        drawing.drawingId,
        nextRole,
      );
      setLinks((current) => ({ ...current, [result.role]: result.shareUrl }));
      setRole(result.role);
      setSharedWithPeople(true);
      onPeopleAccessResolved(drawing.drawingId, true);
    } finally {
      linkPending.current = false;
      setLinkBusy(false);
    }
  };

  const close = useCallback(() => {
    if (!confirmRevoke) onClose();
  }, [confirmRevoke, onClose]);

  const revoke = async () => {
    if (!code || revokePending.current) return;
    revokePending.current = true;
    setRevoking(true);
    setRevokeError(null);
    try {
      await backendClient.revokeAiGrant(await getToken(), code);
      onAiRevoked(code);
      onClose();
    } catch (error) {
      console.error("[GalleryShareDialog] AI access revocation failed:", error);
      setRevokeError("AI access could not be turned off. Please try again.");
    } finally {
      revokePending.current = false;
      setRevoking(false);
    }
  };

  return (
    <>
      <CollabShareDialog
        variant="gallery"
        drawingTitle={drawing.title}
        peopleAccessLoading={isRecipient ? reference.loading : peopleLoading}
        sharedWithPeople={sharedWithPeople}
        peopleSharingKind={peopleSharingKind}
        peopleAccessError={isRecipient ? reference.error : peopleLoadError}
        onRetryPeopleAccess={isRecipient ? reference.retry : retryPeopleAccess}
        peopleAccessRetryLabel={peopleRetryLabel}
        aiAccessLoading={aiLoading}
        aiAccessError={aiLoadError}
        onRetryAiAccess={() => setAiRevision((current) => current + 1)}
        sceneMode="file-backed"
        isCollaborating={false}
        userName=""
        shareUrl={isRecipient ? reference.referenceUrl : (links[role] ?? null)}
        linkKind={isRecipient ? "existing-access" : "sharing"}
        canCreateShareLink={drawing.role === "owner"}
        hasReusablePeopleLink={Object.keys(links).length > 0}
        linkRole={role}
        linkBusy={linkBusy}
        linkError={linkError}
        onLinkRoleChange={(nextRole) => {
          void resolveLink(nextRole).catch((error: unknown) => {
            console.error("[GalleryShareDialog] Link role change failed:", error);
            setLinkError("The sharing link could not be prepared. Select the permission to retry.");
          });
        }}
        onStartSession={() => resolveLink(role)}
        onStopSession={() => undefined}
        onCopyLink={() => {
          const link = isRecipient ? reference.referenceUrl : links[role];
          if (!link) throw new Error("Drawing link is not ready");
          return copyLink(link, drawing.title, isRecipient ? undefined : role);
        }}
        aiCode={code}
        aiAccess={isRecipient ? reference.access : access}
        onShareWithAi={async () => {
          const verifiedAccess = isRecipient ? (await reference.check()).access : undefined;
          const result = await backendClient.mintAiGrant(await getToken(), drawing.drawingId);
          const effectiveAccess = verifiedAccess ?? result.access;
          setCode(result.grant.code);
          setAccess(effectiveAccess);
          onAiShared({ ...drawing, sharedWithPeople }, result.grant, effectiveAccess);
        }}
        onRequestStopAiAccess={() => setConfirmRevoke(true)}
        onConnectAiAssistant={onConnect}
        onClose={close}
      />
      {confirmRevoke && (
        <DestructiveConfirmationDialog
          title="Stop your AI access?"
          confirmLabel="Stop AI access"
          busyLabel="Stopping…"
          busy={revoking}
          error={revokeError}
          testId="gallery-revoke-ai"
          blocking
          onConfirm={() => void revoke()}
          onClose={() => {
            if (!revokePending.current) {
              setConfirmRevoke(false);
              setRevokeError(null);
            }
          }}
        >
          <p style={{ margin: 0 }}>
            This code will stop working. Changes already made stay in the drawing. Anything an
            assistant already opened is unaffected. Other people&apos;s AI access is not changed.
          </p>
        </DestructiveConfirmationDialog>
      )}
    </>
  );
}
